import React, { lazy, Suspense, useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import Nav from "../components/sections/Nav";
import Hero from "../components/sections/Hero";
import Stats from "../components/sections/Stats";
import JsonLd, { JsonLdScript, itemListSchema } from "../components/JsonLd";
import Seo from "../components/Seo";
import Intro from "../components/Intro";
import { SERVICES } from "../data/venpa";

const SERVICES_SCHEMA = itemListSchema(
  "Venpa AI Services",
  SERVICES.map((s) => ({
    item: {
      "@type": "Service",
      name: s.title,
      description: s.desc,
      provider: { "@type": "Organization", name: "Venpa AI" },
    },
  }))
);

/* ---- Below-fold sections: lazy-loaded so they don't block first paint ---- */
const Services = lazy(() => import("../components/sections/Services"));
const Framework = lazy(() => import("../components/sections/Framework"));
const CaseStudies = lazy(() => import("../components/sections/CaseStudies"));
const ROICalculator = lazy(() => import("../components/sections/ROICalculator"));
const Testimonials = lazy(() => import("../components/sections/Testimonials"));
const Blog = lazy(() => import("../components/sections/Blog"));
const FAQ = lazy(() => import("../components/sections/FAQ"));
const Contact = lazy(() => import("../components/sections/Contact"));
const Footer = lazy(() => import("../components/sections/Footer"));
const ChatBubble = lazy(() => import("../components/ChatBubble"));

/* Invisible placeholders while chunks load, sized per-section to roughly
 * match each section's real rendered height — a single shared fallback
 * height (previously 200px for every section) meant heavier sections like
 * Services (12 cards) or Contact (a full form) collapsed to 200px, then
 * jumped to their real height once loaded, causing avoidable CLS. */
function SectionFallback({ minH = "min-h-[200px]" }) {
  return <div className={minH} />;
}

export default function Landing() {
  const [introDone, setIntroDone] = useState(false);
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      prevent: (node) => node.closest('[data-testid="chat-panel"]') !== null,
    });
    lenisRef.current = lenis;

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    if (!introDone) {
      lenis.stop(); // Disable scroll during intro
    } else {
      lenis.start(); // Re-enable scroll when intro completes
    }

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [introDone]);

  // Cross-page nav/footer links (e.g. from /blog to /#services) land here
  // with a hash, but the target sections are React.lazy-loaded and mount
  // asynchronously — the browser's native hash-scroll fires too early and
  // finds nothing. Retry until the target exists, then scroll via the
  // active Lenis instance so it stays in sync with its virtual scroll.
  useEffect(() => {
    if (!introDone || !location.hash) return;
    const id = location.hash.slice(1);
    let attempts = 0;
    let cancelled = false;

    const tryScroll = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -96 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (attempts < 30) {
        attempts += 1;
        setTimeout(tryScroll, 150);
      }
    };
    tryScroll();

    return () => {
      cancelled = true;
    };
  }, [location.hash, introDone]);

  return (
    <div className="min-h-screen bg-ink-light text-ink-soft" data-testid="landing-page">
      <Seo
        title="AI Automation Agency: AI Chatbots & Voice Agents | Venpa AI"
        description="Venpa AI builds AI chatbots, AI voice agents and full-stack automation for high-growth brands in India, the US and UAE. Book a free discovery call today."
        path="/"
      />
      <JsonLd />
      <JsonLdScript id="ld-services" data={SERVICES_SCHEMA} />

      {!introDone && <Intro onComplete={() => setIntroDone(true)} />}

      <Nav />
      <main>
        {/* Above the fold — eagerly loaded */}
        <Hero />
        <Stats />

        {/* Below the fold — lazy loaded. Each section gets its own Suspense
            boundary (rather than one shared boundary around all 8) so their
            chunks fetch in parallel instead of a serial waterfall — under a
            single boundary, React only calls the next lazy import() once
            the previous one has resolved and rendered, since a suspended
            child unwinds the render before reaching its siblings. */}
        <Suspense fallback={<SectionFallback minH="min-h-[700px] lg:min-h-[1100px]" />}>
          <Services />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[420px] lg:min-h-[520px]" />}>
          <Framework />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[700px] lg:min-h-[900px]" />}>
          <ROICalculator />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[500px] lg:min-h-[420px]" />}>
          <CaseStudies />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[480px]" />}>
          <Testimonials />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[500px] lg:min-h-[560px]" />}>
          <Blog />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[600px]" />}>
          <FAQ />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="min-h-[700px]" />}>
          <Contact />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <Footer />
        {/* <ChatBubble /> — hidden for now, re-enable when ready */}
      </Suspense>
    </div>
  );
}
